import { api } from '@/features/auth';

import {
  CreateOpportunityRequest,
  GetOpportunitiesResponse,
  GetOpportunityRequest,
  GetOpportunityResponse,
  GetOpportunityOffersResponse,
  UpdateOpportunityRequest,
} from './types';

export const opportunitiesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMyOpportunities: builder.query<GetOpportunitiesResponse['data'], void>({
      query: () => ({
        url: `items/opportunities`,
        method: 'GET',
        params: {
          fields: [
            'id',
            'created_at',
            'created_by',
            'updated_at',
            'updated_by',
            'status',
            'closed_type',
            'company',
            'name',
            'type',
            'plan',
            'minimum_percentage',
            'maximum_percentage',
            'comments',
            'about_you',
            'vdr_link',

            // Options y files
            'options',
            'files.*',
            'projects.name',

            //TODO: get only specific data:
            'projects.project.*',
            'projects.project.technologies.*.*',
            'projects.project.portfolio_summary.*',
            'projects.project.other_info.type_of_revenue',
            'offers.*.*',
            'opp_summary_stats.key',
          ],
          filter: `{"company": {"users": {"_eq": "$CURRENT_USER"}}, "status": {"_neq": "archived"}}`,
        },
      }),
      transformResponse: ({ data }: GetOpportunitiesResponse) => data,
      providesTags: [{ type: 'Opportunities', id: 'me' }],
    }),
    getOpportunity: builder.query<GetOpportunityResponse['data'], GetOpportunityRequest>({
      query: ({ id }: GetOpportunityRequest) => ({
        url: `items/opportunities/${id}`,
        method: 'GET',
        params: {
          fields: [
            'id',
            'created_at',
            'created_by',
            'updated_at',
            'updated_by',
            'status',
            'closed_type',
            'company',
            'name',
            'type',
            'minimum_percentage',
            'maximum_percentage',
            'comments',
            'about_you',
            'plan',
            'options',
            'vdr_link',

            //TODO: get only specific data:
            'projects.project.*',
            'projects.*',
            'projects.project.technologies.*.*',
            'projects.project.portfolio_summary.*',
            'offers.id,offers.company.id',
            'offers.company.name,files.*.*',
            'advisors.*',
            'advisors.service_companies_id.*',
            'advisors.service_companies_id.tags.*',
            'advisors.service_companies_id.logo.*',
            'advisors.service_companies_id.category.*',
          ],
        },
      }),
      transformResponse: ({ data }: GetOpportunityResponse) => data,
      providesTags: (_result, _error, args) => [{ type: 'Opportunities', id: args.id }],
    }),
    createOpportunity: builder.mutation<GetOpportunityResponse['data'], CreateOpportunityRequest>({
      query: (data: CreateOpportunityRequest) => ({
        url: `items/opportunities`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Opportunities', id: 'me' }],
    }),
    updateOpportunity: builder.mutation<void, UpdateOpportunityRequest>({
      query: ({ id, data }) => ({
        url: `items/opportunities/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Opportunities', id: 'me' },
        { type: 'Opportunities', id: args.id },
      ],
    }),
    getOpportunities: builder.query<GetOpportunitiesResponse['data'], void>({
      query: () => ({
        url: `items/opportunities`,
        method: 'GET',
        params: {
          fields: [
            'id',
            'created_at',
            'created_by',
            'updated_at',
            'updated_by',
            'status',
            'closed_type',
            'company',
            'name',
            'type',
            'minimum_percentage',
            'maximum_percentage',
            'comments',
            'about_you',
            'plan',
            'options',
            'projects.project.country',
            'projects.project.city',
            'projects.project.other_info.type_of_revenue',
            'projects.project.technologies.*.*',
            'projects.project.portfolio_summary.total_mw',
          ],
          filter: '{"status":{"_in":["published","closed"]}}',
          limit: 500,
        },
      }),
      transformResponse: ({ data }: GetOpportunitiesResponse) => data,
      providesTags: [
        { type: 'Opportunities', id: 'published' },
        { type: 'Opportunities', id: 'closed' },
      ],
    }),
    getOpportunityOffers: builder.query<GetOpportunityOffersResponse['data'], GetOpportunityRequest>({
      query: ({ id }: GetOpportunityRequest) => ({
        url: `items/opportunities/${id}`,
        method: 'GET',
        params: {
          //TODO: get only specific data:
          fields: 'offers.*.*',
        },
      }),
      transformResponse: ({ data }: GetOpportunityOffersResponse) => data,
      providesTags: (_result, _error, args) => [{ type: 'Offers', id: args.id }],
    }),
    createOpportunityFileRelation: builder.mutation<any, { opportunity: string; file: string; type: string }>({
      query: (data) => ({
        url: '/items/opportunities_files',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Opportunities', id: 'me' }],
    }),
  }),
});

export const {
  useGetOpportunityQuery,
  useGetMyOpportunitiesQuery,
  useCreateOpportunityMutation,
  useUpdateOpportunityMutation,
  useGetOpportunitiesQuery,
  useGetOpportunityOffersQuery,
  useCreateOpportunityFileRelationMutation,
} = opportunitiesApi;

export type {
  GetOpportunitiesResponse,
  GetOpportunityRequest,
  GetOpportunityResponse,
  GetOpportunityOffersResponse,
} from './types';
